import React, { useEffect, useState } from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Box,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Grid,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import api from '../../utils/api';

interface ManualDeal {
  _id: string;
  asin: string;
  qdrantId: string;
  title: string; // Hebrew
  titleEnglish?: string; // English for reference
  brand?: string;
  price?: {
    displayAmount: string;
    amount: number;
    currency: string;
    savings?: {
      amount: number;
      currency: string;
      displayAmount: string;
      percentage: number;
    };
  };
  imageUrl?: string;
  features?: string[];
  description?: string; // Hebrew
  descriptionEnglish?: string; // English for reference
  material?: string;
  categories?: string[];
  shippingInfo?: string;
  summary?: string;
  isActive: boolean;
  lastUpdated: string;
  createdAt: string;
}

export const ManualDealsManagement: React.FC = () => {
  const [deals, setDeals] = useState<ManualDeal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalDeals, setTotalDeals] = useState(0);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [asinInput, setAsinInput] = useState('');
  const [adding, setAdding] = useState(false);
  const [manualInputDialogOpen, setManualInputDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [manualInput, setManualInput] = useState({
    asin: '',
    amazonLink: '',
    price: '',
    discountPercent: '',
    descriptionEnglish: '',
  });
  const [editingDeal, setEditingDeal] = useState<ManualDeal | null>(null);
  const [addingManual, setAddingManual] = useState(false);
  const [updatingManual, setUpdatingManual] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<ManualDeal | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchDeals();
  }, [page, rowsPerPage]);

  const fetchDeals = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/manual-deals?page=${page + 1}&limit=${rowsPerPage}`);
      if (response.data.success) {
        setDeals(response.data.data.deals);
        setTotalDeals(response.data.data.pagination.total);
      }
    } catch (err) {
      setError('Failed to fetch manual deals');
      console.error('Error fetching manual deals:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!asinInput.trim()) {
      setError('ASIN is required');
      return;
    }

    try {
      setAdding(true);
      setError(null);
      const response = await api.post('/manual-deals', { asin: asinInput.trim() });
      if (response.data.success) {
        setAddDialogOpen(false);
        setAsinInput('');
        await fetchDeals();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add manual deal');
      console.error('Error adding manual deal:', err);
    } finally {
      setAdding(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, deal: ManualDeal) => {
    setAnchorEl(event.currentTarget);
    setSelectedDeal(deal);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = (deal: ManualDeal) => {
    setSelectedDeal(deal);
    setDeleteDialogOpen(true);
    setAnchorEl(null);
  };

  const handleViewDetails = (deal: ManualDeal) => {
    setSelectedDeal(deal);
    setDetailDialogOpen(true);
    setAnchorEl(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedDeal) return;

    try {
      await api.delete(`/manual-deals/${selectedDeal.asin}`);
      setDeals(deals.filter(d => d.asin !== selectedDeal.asin));
      setTotalDeals(totalDeals - 1);
      setDeleteDialogOpen(false);
      setSelectedDeal(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete manual deal');
      console.error('Error deleting manual deal:', err);
    }
  };

  const handleToggle = async (deal: ManualDeal) => {
    try {
      setError(null);
      const response = await api.patch(`/manual-deals/${deal.asin}/toggle`);
      if (response.data.success) {
        // Update the deal in the list
        setDeals(deals.map(d => 
          d.asin === deal.asin 
            ? { ...d, isActive: response.data.data.isActive }
            : d
        ));
        handleMenuClose();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to toggle deal status');
      console.error('Error toggling deal:', err);
      handleMenuClose();
    }
  };

  const handleAddManual = async () => {
    if (!manualInput.asin.trim()) {
      setError('ASIN is required');
      return;
    }
    if (!manualInput.descriptionEnglish.trim()) {
      setError('Description in English is required');
      return;
    }
    if (!manualInput.price || parseFloat(manualInput.price) <= 0) {
      setError('Valid price is required');
      return;
    }

    try {
      setAddingManual(true);
      setError(null);
      const response = await api.post('/manual-deals/from-input', {
        asin: manualInput.asin.trim().toUpperCase(),
        amazonLink: manualInput.amazonLink.trim(),
        price: parseFloat(manualInput.price),
        discountPercent: parseFloat(manualInput.discountPercent) || 0,
        descriptionEnglish: manualInput.descriptionEnglish.trim(),
      });
      if (response.data.success) {
        setManualInputDialogOpen(false);
        setManualInput({
          asin: '',
          amazonLink: '',
          price: '',
          discountPercent: '',
          descriptionEnglish: '',
        });
        await fetchDeals();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add manual deal');
      console.error('Error adding manual deal:', err);
    } finally {
      setAddingManual(false);
    }
  };

  const handleEdit = (deal: ManualDeal) => {
    // Calculate original price and discount from existing price data
    let originalPrice = deal.price?.amount || 0;
    let discountPercent = 0;
    
    if (deal.price?.savings) {
      // If savings exist, calculate original price
      originalPrice = (deal.price.amount || 0) + (deal.price.savings.amount || 0);
      discountPercent = deal.price.savings.percentage || 0;
    }

    setEditingDeal(deal);
    setManualInput({
      asin: deal.asin,
      amazonLink: (deal as any).amazonLink || '', // Get amazonLink from deal if available
      price: originalPrice.toFixed(2),
      discountPercent: discountPercent.toString(),
      descriptionEnglish: deal.descriptionEnglish || '',
    });
    setEditDialogOpen(true);
    setAnchorEl(null);
  };

  const handleUpdateManual = async () => {
    if (!editingDeal) return;
    
    if (!manualInput.price || parseFloat(manualInput.price) <= 0) {
      setError('Valid price is required');
      return;
    }

    try {
      setUpdatingManual(true);
      setError(null);
      const response = await api.put(`/manual-deals/${editingDeal.asin}/from-input`, {
        amazonLink: manualInput.amazonLink.trim(),
        price: parseFloat(manualInput.price),
        discountPercent: parseFloat(manualInput.discountPercent) || 0,
        descriptionEnglish: manualInput.descriptionEnglish.trim() || undefined,
      });
      if (response.data.success) {
        setEditDialogOpen(false);
        setEditingDeal(null);
        setManualInput({
          asin: '',
          amazonLink: '',
          price: '',
          discountPercent: '',
          descriptionEnglish: '',
        });
        await fetchDeals();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update manual deal');
      console.error('Error updating manual deal:', err);
    } finally {
      setUpdatingManual(false);
    }
  };

  const getAmazonUrl = (asin: string) => {
    return `https://www.amazon.com/dp/${asin}`;
  };

  const openAmazonPage = (asin: string) => {
    window.open(getAmazonUrl(asin), '_blank');
  };

  const formatPrice = (price?: { displayAmount?: string; amount?: number; currency?: string }) => {
    if (!price) return 'N/A';
    return price.displayAmount || `${price.amount || 0} ${price.currency || 'USD'}`;
  };

  const handleRefreshAll = async () => {
    if (!window.confirm(`This will refresh all ${totalDeals} manual deals from Amazon. This may take several minutes. Continue?`)) {
      return;
    }

    try {
      setRefreshing(true);
      setError(null);
      const response = await api.post('/manual-deals/refresh-all');
      
      if (response.data.success) {
        const { processed, errors, total } = response.data.data;
        alert(`Refresh completed!\n\nProcessed: ${processed}\nErrors: ${errors}\nTotal: ${total}`);
        await fetchDeals(); // Refresh the list to show updated data
      } else {
        setError(response.data.error || 'Failed to refresh deals');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to refresh all deals');
      console.error('Error refreshing all deals:', err);
    } finally {
      setRefreshing(false);
    }
  };

  if (loading && deals.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Manual Amazon Deals</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
            onClick={handleRefreshAll}
            disabled={refreshing || totalDeals === 0}
          >
            {refreshing ? 'Refreshing...' : 'Refresh All from Amazon'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setManualInputDialogOpen(true)}
          >
            Add Deal Manually
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
          >
            Add Deal by ASIN
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ASIN</TableCell>
              <TableCell>Title (Hebrew)</TableCell>
              <TableCell>Brand</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Last Updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {deals.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No manual deals found. Click "Add Deal by ASIN" to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              deals.map((deal) => (
                <TableRow key={deal._id}>
                  <TableCell>{deal.asin}</TableCell>
                  <TableCell
                    sx={{
                      direction: deal.title ? 'rtl' : 'ltr',
                      textAlign: deal.title ? 'right' : 'left',
                      maxWidth: 300,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {deal.title || 'N/A'}
                  </TableCell>
                  <TableCell>{deal.brand || 'N/A'}</TableCell>
                  <TableCell>{formatPrice(deal.price)}</TableCell>
                  <TableCell>
                    <Chip
                      label={deal.isActive ? 'Active' : 'Inactive'}
                      color={deal.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(deal.lastUpdated).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, deal)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalDeals}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />

      {/* Add Deal Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Manual Deal by ASIN</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              label="ASIN"
              fullWidth
              required
              value={asinInput}
              onChange={(e) => setAsinInput(e.target.value.toUpperCase())}
              margin="normal"
              helperText="Enter Amazon product ASIN (10 alphanumeric characters)"
              inputProps={{
                maxLength: 10,
                pattern: '[A-Z0-9]{10}',
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} disabled={adding}>
            Cancel
          </Button>
          <Button onClick={handleAdd} variant="contained" disabled={adding || !asinInput.trim()}>
            {adding ? <CircularProgress size={20} /> : 'Add Deal'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Deal Manually Dialog */}
      <Dialog open={manualInputDialogOpen} onClose={() => setManualInputDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add Deal Manually</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              label="ASIN"
              fullWidth
              required
              value={manualInput.asin}
              onChange={(e) => setManualInput({ ...manualInput, asin: e.target.value.toUpperCase() })}
              margin="normal"
              helperText="Enter Amazon product ASIN (10 alphanumeric characters)"
              inputProps={{
                maxLength: 10,
                pattern: '[A-Z0-9]{10}',
              }}
            />
            <TextField
              label="Amazon Link"
              fullWidth
              value={manualInput.amazonLink}
              onChange={(e) => setManualInput({ ...manualInput, amazonLink: e.target.value })}
              margin="normal"
              helperText="Enter the full Amazon product URL (optional)"
              placeholder="https://www.amazon.com/dp/..."
            />
            <TextField
              label="Price ($)"
              fullWidth
              required
              type="number"
              value={manualInput.price}
              onChange={(e) => setManualInput({ ...manualInput, price: e.target.value })}
              margin="normal"
              helperText="Enter the product price in USD"
              inputProps={{
                min: 0,
                step: 0.01,
              }}
            />
            <TextField
              label="Discount Percent (%)"
              fullWidth
              type="number"
              value={manualInput.discountPercent}
              onChange={(e) => setManualInput({ ...manualInput, discountPercent: e.target.value })}
              margin="normal"
              helperText="Enter discount percentage (0-100)"
              inputProps={{
                min: 0,
                max: 100,
                step: 1,
              }}
            />
            <TextField
              label="Description (English)"
              fullWidth
              required
              multiline
              rows={4}
              value={manualInput.descriptionEnglish}
              onChange={(e) => setManualInput({ ...manualInput, descriptionEnglish: e.target.value })}
              margin="normal"
              helperText="Enter product description in English. This will be translated to Hebrew automatically."
              placeholder="Enter a detailed product description in English..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualInputDialogOpen(false)} disabled={addingManual}>
            Cancel
          </Button>
          <Button 
            onClick={handleAddManual} 
            variant="contained" 
            disabled={addingManual || !manualInput.asin.trim() || !manualInput.descriptionEnglish.trim() || !manualInput.price}
          >
            {addingManual ? <CircularProgress size={20} /> : 'Add Deal'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Deal Manually Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Deal Manually</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              label="ASIN"
              fullWidth
              disabled
              value={manualInput.asin}
              margin="normal"
              helperText="ASIN cannot be changed"
            />
            <TextField
              label="Amazon Link"
              fullWidth
              value={manualInput.amazonLink}
              onChange={(e) => setManualInput({ ...manualInput, amazonLink: e.target.value })}
              margin="normal"
              helperText="Enter the full Amazon product URL (optional)"
              placeholder="https://www.amazon.com/dp/..."
            />
            <TextField
              label="Price ($)"
              fullWidth
              required
              type="number"
              value={manualInput.price}
              onChange={(e) => setManualInput({ ...manualInput, price: e.target.value })}
              margin="normal"
              helperText="Enter the original product price in USD (before discount)"
              inputProps={{
                min: 0,
                step: 0.01,
              }}
            />
            <TextField
              label="Discount Percent (%)"
              fullWidth
              type="number"
              value={manualInput.discountPercent}
              onChange={(e) => setManualInput({ ...manualInput, discountPercent: e.target.value })}
              margin="normal"
              helperText="Enter discount percentage (0-100)"
              inputProps={{
                min: 0,
                max: 100,
                step: 1,
              }}
            />
            <TextField
              label="Description (English)"
              fullWidth
              multiline
              rows={4}
              value={manualInput.descriptionEnglish}
              onChange={(e) => setManualInput({ ...manualInput, descriptionEnglish: e.target.value })}
              margin="normal"
              helperText="Enter product description in English. This will be translated to Hebrew automatically. Leave empty to keep existing description."
              placeholder="Enter a detailed product description in English..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditDialogOpen(false);
            setEditingDeal(null);
            setManualInput({
              asin: '',
              amazonLink: '',
              price: '',
              discountPercent: '',
              descriptionEnglish: '',
            });
          }} disabled={updatingManual}>
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateManual} 
            variant="contained" 
            disabled={updatingManual || !manualInput.price}
          >
            {updatingManual ? <CircularProgress size={20} /> : 'Update Deal'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleViewDetails(selectedDeal!)}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => openAmazonPage(selectedDeal!.asin)}>
          <ListItemIcon>
            <OpenInNewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View on Amazon</ListItemText>
        </MenuItem>
        {selectedDeal && (
          <MenuItem onClick={() => handleEdit(selectedDeal)}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit Manually</ListItemText>
          </MenuItem>
        )}
        {selectedDeal && (
          <MenuItem onClick={() => handleToggle(selectedDeal)}>
            <ListItemIcon>
              {selectedDeal.isActive ? (
                <ToggleOnIcon fontSize="small" color="success" />
              ) : (
                <ToggleOffIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText>
              {selectedDeal.isActive ? 'Disable Deal' : 'Enable Deal'}
            </ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => handleDelete(selectedDeal!)}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => {
          setDetailDialogOpen(false);
          setSelectedDeal(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ direction: selectedDeal?.title ? 'rtl' : 'ltr', textAlign: selectedDeal?.title ? 'right' : 'left' }}>
          {selectedDeal?.title || 'Deal Details'}
        </DialogTitle>
        <DialogContent>
          {selectedDeal ? (
            <Box sx={{ mb: 2 }}>
              {selectedDeal.imageUrl && (
                <Box sx={{ mb: 2, textAlign: 'center' }}>
                  <img
                    src={selectedDeal.imageUrl}
                    alt={selectedDeal.title}
                    style={{ maxWidth: '100%', maxHeight: 300, objectFit: 'contain' }}
                  />
                </Box>
              )}

              {/* Hebrew Description */}
              {selectedDeal.description && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    תיאור (Description):
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      direction: 'rtl',
                      textAlign: 'right',
                      mb: 2,
                      whiteSpace: 'pre-wrap'
                    }}
                  >
                    {selectedDeal.description}
                  </Typography>
                </Box>
              )}

              {/* English Reference */}
              {(selectedDeal.titleEnglish || selectedDeal.descriptionEnglish) && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    English (for reference):
                  </Typography>
                  {selectedDeal.titleEnglish && (
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                      {selectedDeal.titleEnglish}
                    </Typography>
                  )}
                  {selectedDeal.descriptionEnglish && (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedDeal.descriptionEnglish}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Additional Details */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">ASIN:</Typography>
                  <Typography variant="body2">{selectedDeal.asin}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">Brand:</Typography>
                  <Typography variant="body2">{selectedDeal.brand || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">Price:</Typography>
                  <Typography variant="body2">{formatPrice(selectedDeal.price)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">Shipping:</Typography>
                  <Typography variant="body2">{selectedDeal.shippingInfo || 'N/A'}</Typography>
                </Grid>
                {selectedDeal.features && selectedDeal.features.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary">Features:</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {selectedDeal.features.map((feature, index) => (
                        <Chip key={index} label={feature} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Grid>
                )}
                {selectedDeal.categories && selectedDeal.categories.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary">Categories:</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {selectedDeal.categories.map((category, index) => (
                        <Chip key={index} label={category} size="small" />
                      ))}
                    </Box>
                  </Grid>
                )}
                {selectedDeal.summary && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary">Summary:</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {selectedDeal.summary}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          ) : (
            <Typography>No deal selected</Typography>
          )}
        </DialogContent>
        <DialogActions>
          {selectedDeal && (
            <Button
              startIcon={<OpenInNewIcon />}
              onClick={() => openAmazonPage(selectedDeal.asin)}
              variant="outlined"
            >
              View on Amazon
            </Button>
          )}
          <Button onClick={() => {
            setDetailDialogOpen(false);
            setSelectedDeal(null);
          }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Manual Deal</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this manual deal?
          </Typography>
          {selectedDeal && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2"><strong>ASIN:</strong> {selectedDeal.asin}</Typography>
              <Typography variant="body2"><strong>Title:</strong> {selectedDeal.title}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

